import type {
  CameraBearingMode,
  CameraMode,
  CameraPlan,
  CameraShot,
  RouteGeometry,
} from "./cameraPlan";
import { cumulativeMeters, cursorAtFraction, type LngLat } from "./cameraRoute";

/**
 * SmoothingSigmas are the distance-domain Gaussian σ values (in route metres) a
 * mode smooths its camera channels with: `bearing` for the heading channel,
 * `pitchZoom` for the framing channels, and `center` for the camera's ground path
 * (lng/lat). The σ is a route-distance, not a wall-clock span, so the smoothing
 * window covers the same fraction of the trail regardless of how long the flight
 * runs — a 10 s and a 60 s flight over the same route smooth identically rather
 * than a fixed-seconds window over-smoothing the short one. The centre σ is the
 * widest — the look-at point should glide steadily even when the route has many
 * tiny turns or GPS noise — and the bearing σ the tightest, so the camera tracks
 * heading sooner than it re-frames. The track resolver converts each σ to frame
 * samples per route via sigmaFramesForMeters before the Gaussian pass; the three
 * channels are tuned separately.
 */
export interface SmoothingSigmas {
  bearing: number;
  center: number;
  pitchZoom: number;
}

/**
 * MODE_SMOOTHING_SIGMAS is the per-mode Gaussian σ matrix (in route metres) the
 * resolver applies before the spline pass. `chase` is the tightest (it banks hard,
 * the nervous action-cam energy), `cinematic` close behind, `smart` between,
 * `overview` steady, `drone` and `helicopter` the loosest (a floaty aerial glide
 * and a heavy broadcast gimbal, smoothing over the most route-distance);
 * `director`, whose shots are authored explicitly, gets the same steady treatment
 * as cinematic so a hand-placed sequence reads smoothly. The per-channel ordering
 * holds across every mode (bearing < pitchZoom < center) so heading tracks sooner
 * than framing, which tracks sooner than the ground path. Held as a constant
 * table so a mode's feel is one literal to tune.
 */
export const MODE_SMOOTHING_SIGMAS: Record<CameraMode, SmoothingSigmas> = {
  chase: { bearing: 40, center: 100, pitchZoom: 70 },
  cinematic: { bearing: 50, center: 120, pitchZoom: 80 },
  director: { bearing: 50, center: 120, pitchZoom: 80 },
  drone: { bearing: 220, center: 350, pitchZoom: 280 },
  helicopter: { bearing: 200, center: 300, pitchZoom: 240 },
  overview: { bearing: 140, center: 260, pitchZoom: 200 },
  smart: { bearing: 110, center: 200, pitchZoom: 150 },
};

/**
 * SIGMA_FRAMES_MIN floors the frame-sample σ a route-metre σ converts to, so even
 * a very short flight (few frames over a long route) still applies a light
 * smoothing pass rather than collapsing to a no-op. ~2 samples is the smallest
 * window the Gaussian kernel rounds anything with.
 */
const SIGMA_FRAMES_MIN = 2;

/**
 * DEGENERATE_MEAN_EPS floors the magnitude of the smoothed-heading mean vector
 * below which smoothCircularSeries treats the circular average as undefined: when
 * a window's headings nearly cancel (antipodal samples), the mean unit vector
 * shrinks toward the origin and the angle atan2 recovers from it whips between
 * unrelated directions. ~1e-3 catches the near-cancellation while leaving any
 * genuinely-directed window (magnitude near 1) untouched.
 */
const DEGENERATE_MEAN_EPS = 1e-3;

/**
 * SIGMA_FRAMES_FALLBACK_DIVISOR sets the frame-sample σ when the route length is
 * unknown (a degenerate zero-length route): σ becomes `frameCount / divisor`, a
 * moderate window proportional to the grid, so the smoothing stays sane rather
 * than dividing by zero. The same divisor caps the converted σ at `frameCount / 3`
 * regardless of route length, so the window never swallows the whole flight.
 */
const SIGMA_FRAMES_FALLBACK_DIVISOR = 3;

/**
 * sigmaFramesForMeters converts a distance-domain σ (in route metres) to the σ in
 * frame samples the Gaussian passes take, so the smoothing window spans a fixed
 * route-distance no matter the flight duration: `sigmaMeters * frameCount /
 * routeLengthMeters`, since the dense per-frame grid spreads `frameCount` samples
 * across `routeLengthMeters` of trail. The result is clamped to
 * `[SIGMA_FRAMES_MIN, frameCount / 3]` — a small floor so a short flight still
 * smooths, and a third-of-the-grid ceiling so the window never spans the whole
 * route. A non-positive or non-finite route length (a degenerate route) falls back
 * to `frameCount / 3` so the division never blows up.
 */
export const sigmaFramesForMeters = (
  sigmaMeters: number,
  frameCount: number,
  routeLengthMeters: number,
): number => {
  const ceil = Math.max(SIGMA_FRAMES_MIN, frameCount / SIGMA_FRAMES_FALLBACK_DIVISOR);
  if (!Number.isFinite(routeLengthMeters) || routeLengthMeters <= 0) {
    return ceil;
  }

  const frames = (sigmaMeters * frameCount) / routeLengthMeters;

  return Math.min(ceil, Math.max(SIGMA_FRAMES_MIN, frames));
};

/**
 * CameraSpline is the evaluable form of a Camera Plan over a route: ordered
 * keyframe progress fractions (`fractions`, ascending in 0..1) paired with the
 * smoothed camera channels (`bearings`, `pitches`, `zooms` in degrees / zoom
 * level), each keyframe's `modes` (how its bearing is chosen), the `line` the
 * centre is sampled from, its precomputed `cumulative` arc-length, and the
 * `lookaheadMeters` the centre tangent uses. evalSpline reads a pose at any
 * normalized parameter; the centre is taken from the route (the ground truth)
 * while pitch/zoom are interpolated between keyframes and the bearing is resolved
 * from the bracketing keyframes' modes — a `follow` leg tracks the live route
 * tangent at the frame, a non-`follow` leg interpolates the authored keyframe
 * bearings, and a mixed leg blends the two.
 */
export interface CameraSpline {
  bearings: number[];
  cumulative: number[];
  fractions: number[];
  line: LngLat[];
  lookaheadMeters: number;
  modes: CameraBearingMode[];
  pitches: number[];
  zooms: number[];
}

const TO_RAD = Math.PI / 180;
const TO_DEG = 180 / Math.PI;

/**
 * uniformCatmullRom evaluates a uniform Catmull-Rom spline through the (knot,
 * value) control points at the requested knot, returning the interpolated value.
 * It uses the Barry-Goldman triangulation, which is C¹ continuous and, on the
 * evenly-spaced knots the camera channels use, reproduces standard Catmull-Rom.
 * Fewer than four control points fall back to linear interpolation (a Catmull-Rom
 * segment needs four neighbours); an out-of-range knot clamps to the nearest
 * endpoint value rather than extrapolating.
 *
 * A degenerate span — the clamped neighbour index at the FIRST/LAST segment makes
 * t0 == t1 (or t2 == t3), and a duplicated interior knot can do the same — takes
 * the blend's analytic limit (the surviving control value) instead of dividing.
 * Dividing by an epsilon there is NOT a guard: ((t1-u)·p0 + (u-t0)·p1)/eps
 * evaluates to ~0 for p0 == p1 (the numerator cancels), and that phantom zero
 * blends into the whole first and last segments — the camera zoom/pitch/bearing
 * all dove toward 0 across the opening and closing legs of every flight.
 */
export const uniformCatmullRom = (
  knots: readonly number[],
  values: readonly number[],
  target: number,
): number => {
  const n = knots.length;
  if (n === 0) {
    return 0;
  }

  if (n === 1) {
    return values[0] ?? 0;
  }

  const firstKnot = knots[0] ?? 0;
  const lastKnot = knots[n - 1] ?? 0;
  if (target <= firstKnot) {
    return values[0] ?? 0;
  }

  if (target >= lastKnot) {
    return values[n - 1] ?? 0;
  }

  const i1 = segmentIndex(knots, target);
  if (n < 4) {
    const t0 = knots[i1] ?? 0;
    const t1 = knots[i1 + 1] ?? t0;
    const v0 = values[i1] ?? 0;
    const v1 = values[i1 + 1] ?? v0;
    const span = t1 - t0;
    const u = span > 0 ? (target - t0) / span : 0;

    return v0 + (v1 - v0) * u;
  }

  const i0 = Math.max(0, i1 - 1);
  const i2 = Math.min(n - 1, i1 + 1);
  const i3 = Math.min(n - 1, i1 + 2);
  const t0 = knots[i0] ?? 0;
  const t1 = knots[i1] ?? 0;
  const t2 = knots[i2] ?? 0;
  const t3 = knots[i3] ?? 0;
  const p0 = values[i0] ?? 0;
  const p1 = values[i1] ?? 0;
  const p2 = values[i2] ?? 0;
  const p3 = values[i3] ?? 0;
  const eps = 1e-6;
  const u = target;
  const a1 = t1 - t0 > eps ? ((t1 - u) * p0 + (u - t0) * p1) / (t1 - t0) : p1;
  const a2 = t2 - t1 > eps ? ((t2 - u) * p1 + (u - t1) * p2) / (t2 - t1) : p1;
  const a3 = t3 - t2 > eps ? ((t3 - u) * p2 + (u - t2) * p3) / (t3 - t2) : p2;
  const b1 = t2 - t0 > eps ? ((t2 - u) * a1 + (u - t0) * a2) / (t2 - t0) : a2;
  const b2 = t3 - t1 > eps ? ((t3 - u) * a2 + (u - t1) * a3) / (t3 - t1) : a2;

  return t2 - t1 > eps ? ((t2 - u) * b1 + (u - t1) * b2) / (t2 - t1) : b1;
};

/**
 * unwrapBearings turns a sequence of compass headings (each in 0..360, wrapping
 * at 360/0) into a continuous, monotone-friendly series that a scalar spline can
 * interpolate without the wrap discontinuity. Starting from the first heading, it
 * accumulates the shortest signed arc to each next heading (in (-180, 180]),
 * adding it to the running unwrapped value, so a 350 → 10 step becomes 350 → 370
 * (a +20 step) rather than a −340 plunge, and an antipodal 90 → 270 step resolves
 * to a deterministic +180 (the positive arc on a 180° tie). Splining the
 * unwrapped values and then taking the result mod 360 interpolates a heading the
 * short way and through the consistent direction the unwrap chose, instead of
 * collapsing the way independent cos/sin splines do at antipodal keyframes. An
 * empty input returns empty; a single heading returns itself.
 */
export const unwrapBearings = (bearings: readonly number[]): number[] => {
  const length = bearings.length;
  if (length === 0) {
    return [];
  }

  const out = new Array<number>(length);
  let running = bearings[0] ?? 0;
  out[0] = running;
  for (let i = 1; i < length; i += 1) {
    const previous = bearings[i - 1] ?? 0;
    const current = bearings[i] ?? previous;
    running += shortestArcDelta(previous, current);
    out[i] = running;
  }

  return out;
};

/**
 * shortestArcDelta returns the signed shortest arc in degrees from `from` to `to`,
 * in the half-open range (-180, 180] so that a 180° tie resolves to the positive
 * arc deterministically. The raw forward difference is taken mod 360 into
 * [0, 360), then folded above 180 to its negative complement.
 */
const shortestArcDelta = (from: number, to: number): number => {
  const raw = (((to - from) % 360) + 360) % 360;

  return raw > 180 ? raw - 360 : raw;
};

/**
 * smoothSeries applies a Gaussian low-pass to a uniformly-spaced scalar series
 * (the pitch and zoom channels). σ is in samples; the kernel half-width is 3σ
 * (contributions beyond that are under 0.5 % of the peak). Edge samples reuse the
 * available sub-window without padding, so the series' ends stay anchored to
 * their input values. A non-finite or absent entry is skipped in the weighting.
 * An empty series returns empty.
 */
export const smoothSeries = (values: readonly number[], sigmaSamples: number): number[] => {
  const length = values.length;
  if (length === 0) {
    return [];
  }

  const sigma = Math.max(1e-3, sigmaSamples);
  const halfWidth = Math.max(1, Math.ceil(sigma * 3));
  const denom = 2 * sigma * sigma;
  const out = new Array<number>(length);
  for (let i = 0; i < length; i += 1) {
    const from = Math.max(0, i - halfWidth);
    const to = Math.min(length - 1, i + halfWidth);
    let weighted = 0;
    let weightSum = 0;
    for (let j = from; j <= to; j += 1) {
      const value = values[j];
      if (value === undefined || !Number.isFinite(value)) {
        continue;
      }

      const d = j - i;
      const weight = Math.exp(-(d * d) / denom);
      weighted += value * weight;
      weightSum += weight;
    }

    out[i] = weightSum > 0 ? weighted / weightSum : (values[i] ?? 0);
  }

  return out;
};

/**
 * smoothPositionSeries low-passes a steadily-advancing coordinate channel (a
 * camera centre's lng or lat over the frame grid) and then re-pins its endpoints.
 * A plain Gaussian pass over an advancing series pulls its ends inward — the
 * symmetric window at frame 0 only sees later, further-along samples — which would
 * start the flight partway along the route and end it short of the finish. Adding
 * a linear correction that fades the start error at frame 0 into the end error at
 * the last frame restores the exact route endpoints while leaving the interior
 * smoothed, so the camera travels the whole route yet glides over the
 * high-frequency wiggle of many tiny turns. σ is in samples; a series shorter than
 * two is returned unchanged.
 */
export const smoothPositionSeries = (values: readonly number[], sigmaSamples: number): number[] => {
  const length = values.length;
  if (length < 2) {
    return [...values];
  }

  const smoothed = smoothSeries(values, sigmaSamples);
  const lastIndex = length - 1;
  const startError = (values[0] ?? 0) - (smoothed[0] ?? 0);
  const endError = (values[lastIndex] ?? 0) - (smoothed[lastIndex] ?? 0);

  return smoothed.map((value, index) => {
    const t = index / lastIndex;

    return (value ?? 0) + startError * (1 - t) + endError * t;
  });
};

/**
 * smoothCircularSeries is the bearing-aware Gaussian low-pass: each angle is
 * projected to the unit circle, the (cos, sin) pair is averaged with the same
 * Gaussian weights as smoothSeries, and atan2 recovers the mean angle in 0..360.
 * Averaging on the circle handles the 0/360 wrap — a linear mean of [350, 10]
 * would wrongly land on 180, whereas the circular mean lands on 0. When the
 * window's mean vector nearly cancels (its magnitude is below DEGENERATE_MEAN_EPS,
 * the antipodal case where the angle atan2 recovers is unstable), the previous
 * output value is carried forward instead — the raw value at the first frame — so
 * a switchback whose headings average to no direction does not snap to an
 * arbitrary atan2 result. σ is in samples; an empty series returns empty.
 */
export const smoothCircularSeries = (values: readonly number[], sigmaSamples: number): number[] => {
  const length = values.length;
  if (length === 0) {
    return [];
  }

  const sigma = Math.max(1e-3, sigmaSamples);
  const halfWidth = Math.max(1, Math.ceil(sigma * 3));
  const denom = 2 * sigma * sigma;
  const out = new Array<number>(length);
  for (let i = 0; i < length; i += 1) {
    const from = Math.max(0, i - halfWidth);
    const to = Math.min(length - 1, i + halfWidth);
    let sumCos = 0;
    let sumSin = 0;
    let weightSum = 0;
    for (let j = from; j <= to; j += 1) {
      const value = values[j];
      if (value === undefined || !Number.isFinite(value)) {
        continue;
      }

      const d = j - i;
      const weight = Math.exp(-(d * d) / denom);
      const r = value * TO_RAD;
      sumCos += Math.cos(r) * weight;
      sumSin += Math.sin(r) * weight;
      weightSum += weight;
    }

    if (weightSum === 0) {
      out[i] = values[i] ?? 0;

      continue;
    }

    // The mean unit vector's magnitude collapses toward 0 when the window's
    // headings cancel (antipodal samples). atan2 of a near-zero vector is
    // numerically unstable and would snap to an arbitrary angle, so carry the
    // previous output (the raw first value at the start) rather than trust it.
    const meanCos = sumCos / weightSum;
    const meanSin = sumSin / weightSum;
    if (Math.hypot(meanCos, meanSin) < DEGENERATE_MEAN_EPS) {
      out[i] = i === 0 ? (values[0] ?? 0) : (out[i - 1] ?? 0);

      continue;
    }

    const mean = Math.atan2(meanSin, meanCos) * TO_DEG;
    out[i] = ((mean % 360) + 360) % 360;
  }

  return out;
};

/**
 * buildCameraSpline assembles an evaluable CameraSpline from a plan's shots over
 * a route. Shots are read in ascending `at` order (the plan invariant); their
 * raw pitch, zoom, and resolved-bearing channels are stored against their
 * progress fractions so evalSpline interpolates between them, which means the
 * camera passes exactly through each authored keyframe. The bearing channel is
 * resolved per shot: a `value` shot uses its explicit bearing, a `follow` shot
 * the route tangent at its progress, and a `locked` shot the route's overall
 * first-to-last heading. Time-domain smoothing is intentionally NOT applied here
 * — the keyframe array is too sparse for a Gaussian pass to anchor its endpoints
 * — it is applied by the track resolver over the dense per-frame grid instead
 * (see resolveCameraTrack), so a keyframe stays a keyframe while the motion
 * between frames is smoothed.
 */
export const buildCameraSpline = (
  shots: readonly CameraShot[],
  route: RouteGeometry,
  mode: CameraMode = "smart",
): CameraSpline => {
  const line = route.line.map((coordinate): LngLat => [coordinate[0], coordinate[1]]);
  const cumulative = cumulativeMeters(line);
  const lookaheadMeters = lookaheadForMode(mode);
  const overallHeading = cursorAtFraction(
    line,
    0,
    Math.max(lookaheadMeters, cumulative[cumulative.length - 1] ?? 0),
  ).bearingDeg;

  const fractions: number[] = [];
  const pitches: number[] = [];
  const zooms: number[] = [];
  const bearings: number[] = [];
  const modes: CameraBearingMode[] = [];
  for (const shot of shots) {
    fractions.push(shot.at);
    pitches.push(shot.pitchDeg);
    zooms.push(shot.zoom);
    bearings.push(resolveShotBearing(shot, line, cumulative, lookaheadMeters, overallHeading));
    modes.push(shot.bearingMode);
  }

  return { bearings, cumulative, fractions, line, lookaheadMeters, modes, pitches, zooms };
};

/**
 * evalSpline returns the camera pose at a normalized parameter (tNorm in 0..1,
 * clamped) of a built spline. The centre is sampled from the route at that
 * progress (the route is the ground truth, so the camera never drifts off the
 * path); pitch and zoom are read from the smoothed channels by Catmull-Rom over
 * the keyframe fractions. The bearing is resolved from the bracketing keyframes'
 * modes: a both-`follow` leg uses the live route tangent already computed for the
 * centre (so the heading truly follows the path frame by frame rather than only
 * sampling it at sparse keyframes), with `followWeight` 1; a both-non-`follow`
 * leg interpolates the authored keyframe bearings through unwrapped angles (so an
 * antipodal pair interpolates the short, consistent way instead of collapsing the
 * way independent cos/sin splines do), with `followWeight` 0; a mixed leg blends
 * the keyframe bearing toward the tangent with a smoothstep oriented so the
 * weight is exactly 0 at the non-`follow` keyframe — a locked or value shot is hit
 * exactly at its own `at`, then eases into the follow. A spline with no keyframes
 * resolves to a neutral overhead-ish pose following the route tangent
 * (`followWeight` 1), so a degenerate plan still yields a usable pose.
 */
export const evalSpline = (spline: CameraSpline, tNorm: number): CameraPoseLite => {
  const clamped = Math.min(1, Math.max(0, tNorm));
  const cursor = cursorAtFraction(spline.line, clamped, spline.lookaheadMeters, spline.cumulative);
  if (spline.fractions.length === 0) {
    return {
      bearing: cursor.bearingDeg,
      center: cursor.point,
      followWeight: 1,
      pitch: 60,
      zoom: 14,
    };
  }

  const pitch = clampPitch(uniformCatmullRom(spline.fractions, spline.pitches, clamped));
  const zoom = clampZoom(uniformCatmullRom(spline.fractions, spline.zooms, clamped));
  const { bearing, followWeight } = resolveEvalBearing(spline, clamped, cursor.bearingDeg);

  return { bearing, center: cursor.point, followWeight, pitch, zoom };
};

/**
 * resolveEvalBearing resolves the heading and its follow weight at a clamped
 * progress on a built spline by reading the modes of the keyframes bracketing
 * that progress. Both `follow` → the live tangent at weight 1; both non-`follow` →
 * the unwrapped-angle keyframe interpolation at weight 0; mixed → a circular blend
 * of the keyframe bearing toward the tangent with a smoothstep weight oriented to
 * be 0 at the non-`follow` end (so an authored locked/value bearing is hit exactly
 * at its keyframe). Outside the keyframe span (before the first or after the last)
 * the single bracketing keyframe's mode governs.
 */
const resolveEvalBearing = (
  spline: CameraSpline,
  clamped: number,
  tangentDeg: number,
): { bearing: number; followWeight: number } => {
  const keyframeBearing =
    ((uniformCatmullRom(spline.fractions, unwrapBearings(spline.bearings), clamped) % 360) + 360) %
    360;
  const segment = bearingSegment(spline.fractions, clamped);
  const fromMode = spline.modes[segment.fromIndex] ?? "follow";
  const toMode = spline.modes[segment.toIndex] ?? fromMode;
  const fromFollows = fromMode === "follow";
  const toFollows = toMode === "follow";

  if (fromFollows && toFollows) {
    return { bearing: tangentDeg, followWeight: 1 };
  }

  if (!fromFollows && !toFollows) {
    return { bearing: keyframeBearing, followWeight: 0 };
  }

  // Mixed leg: orient the smoothstep so its weight is 0 at the non-follow end,
  // which keeps a locked or value shot exactly on its authored bearing at its own
  // `at` and eases toward the live tangent across the leg. weight is the follow
  // share, so the blend goes keyframeBearing (weight 0) -> tangent (weight 1).
  const weight = toFollows ? smoothstep(segment.u) : smoothstep(1 - segment.u);

  return { bearing: blendBearing(keyframeBearing, tangentDeg, weight), followWeight: weight };
};

/**
 * BearingSegment locates a clamped progress within the keyframe fractions for the
 * bearing blend: the indices of the keyframes on either side (`fromIndex` <
 * `toIndex`, collapsing to the same index outside the span) and `u`, the 0..1
 * position of the progress between them (0 at `fromIndex`, 1 at `toIndex`).
 */
interface BearingSegment {
  fromIndex: number;
  toIndex: number;
  u: number;
}

/**
 * bearingSegment finds the keyframe pair bracketing a clamped progress and the
 * normalized position between them. Before the first keyframe both indices are 0
 * and u is 0; at or after the last both are the final index and u is 1; inside the
 * span it is the segmentIndex pair with u the fraction of the way across.
 */
const bearingSegment = (fractions: readonly number[], clamped: number): BearingSegment => {
  const n = fractions.length;
  const lastIndex = n - 1;
  const first = fractions[0] ?? 0;
  const last = fractions[lastIndex] ?? 0;
  if (clamped <= first) {
    return { fromIndex: 0, toIndex: 0, u: 0 };
  }

  if (clamped >= last) {
    return { fromIndex: lastIndex, toIndex: lastIndex, u: 1 };
  }

  const fromIndex = segmentIndex(fractions, clamped);
  const toIndex = Math.min(lastIndex, fromIndex + 1);
  const fromFraction = fractions[fromIndex] ?? 0;
  const toFraction = fractions[toIndex] ?? fromFraction;
  const span = toFraction - fromFraction;
  const u = span > 0 ? (clamped - fromFraction) / span : 0;

  return { fromIndex, toIndex, u };
};

/**
 * blendBearing circularly interpolates from `a` toward `b` by weight `w` (0..1),
 * going the shortest way around the circle, and returns the result in 0..360. At
 * w=0 it is `a`, at w=1 it is `b`; it adds `w` of the shortest signed arc from
 * `a` to `b` rather than averaging cos/sin, so the blend never collapses on an
 * antipodal pair.
 */
const blendBearing = (a: number, b: number, w: number): number => {
  const result = a + shortestArcDelta(a, b) * w;

  return ((result % 360) + 360) % 360;
};

/**
 * smoothstep is the C¹ Hermite ease `3t² − 2t³` clamped to [0, 1]: a smooth 0→1
 * ramp with zero slope at both ends, used to fade the bearing blend in over a
 * mixed leg without a velocity kink at the keyframe.
 */
const smoothstep = (t: number): number => {
  const x = Math.min(1, Math.max(0, t));

  return x * x * (3 - 2 * x);
};

/**
 * planDurationMs is the wall-clock length a plan plays for in milliseconds: its
 * base `targetDurationMs` plus the sum of every shot's `holdMs` (each hold pauses
 * the camera at its keyframe for that long on top of the travelling time). This
 * is the duration the track resolver sizes its per-frame grid from, so the frame
 * count reflects the holds the user added.
 */
export const planDurationMs = (plan: CameraPlan): number => {
  let holds = 0;
  for (const shot of plan.shots) {
    holds += shot.holdMs;
  }

  return plan.targetDurationMs + holds;
};

/**
 * CameraPoseLite is the pose evalSpline returns: the same framing fields as
 * CameraPlan's CameraPose plus `followWeight`, the 0..1 degree to which this
 * frame's bearing is driven by the live route tangent (1 on a both-`follow` leg,
 * 0 on a both-non-`follow` leg, the smoothstep blend across a mixed leg). The
 * track resolver reads `followWeight` to gate the athlete-framing lead to
 * following frames only, so a locked or value-bearing shot is never led. It is
 * kept as a local interface so this module does not couple its return type to the
 * plan's exported CameraPose while staying field-compatible on the framing
 * fields.
 */
export interface CameraPoseLite {
  bearing: number;
  center: LngLat;
  followWeight: number;
  pitch: number;
  zoom: number;
}

/**
 * lookaheadForMode is the route-tangent lookahead distance in metres a mode
 * derives `follow` bearings with. A wider lookahead yields a steadier, less
 * reactive heading (drone the steadiest, then helicopter's broadcast pan and
 * overview's calm dive), a tighter one a more responsive camera (chase banks the
 * hardest, cinematic close behind); smart and director sit between.
 */
const lookaheadForMode = (mode: CameraMode): number => {
  switch (mode) {
    case "drone":
      return 340;
    case "helicopter":
      return 200;
    case "overview":
      return 150;
    case "cinematic":
      return 70;
    case "chase":
      return 45;
    default:
      return 120;
  }
};

/**
 * resolveShotBearing returns the heading in degrees a shot frames with: its
 * explicit `bearingDeg` for a `value` shot (or 0 when none was supplied), the
 * route's overall first-to-last heading for a `locked` shot, and the route
 * tangent at the shot's progress for a `follow` shot.
 */
const resolveShotBearing = (
  shot: CameraShot,
  line: readonly LngLat[],
  cumulative: readonly number[],
  lookaheadMeters: number,
  overallHeading: number,
): number => {
  if (shot.bearingMode === "value") {
    return shot.bearingDeg ?? 0;
  }

  if (shot.bearingMode === "locked") {
    return overallHeading;
  }

  return cursorAtFraction(line, shot.at, lookaheadMeters, cumulative).bearingDeg;
};

/** segmentIndex finds i with knots[i] <= target < knots[i + 1] via binary search. */
const segmentIndex = (knots: readonly number[], target: number): number => {
  let lo = 0;
  let hi = knots.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    const value = knots[mid];
    if (value === undefined) {
      break;
    }

    if (value <= target) {
      lo = mid;
    } else {
      hi = mid;
    }
  }

  return lo;
};

/** clampPitch holds a pitch within MapLibre's [0, 85] tilt range, mapping NaN to 60. */
const clampPitch = (value: number): number =>
  Math.max(0, Math.min(85, Number.isFinite(value) ? value : 60));

/** clampZoom holds a zoom within the [0, 22] range, mapping NaN to a sensible mid zoom. */
const clampZoom = (value: number): number =>
  Math.max(0, Math.min(22, Number.isFinite(value) ? value : 14));
